// Phase 708b — Document Approval admin panel.
// Drop-in component for the Annual Statement page (and any other doc
// that uses the generic /api/document-approvals endpoints).
//
// Renders:
//   • Status badge (Draft / Sent / Viewed / Approved / Declined /
//     Review Requested / Cancelled / Expired / Superseded)
//   • Recipient + Last Action summary row
//   • Admin actions: View History / Approval Details / Resend / Cancel
//   • Approval Details modal — full audit trail per version
//   • Approval History modal — list of every version with statuses
//
// Backend endpoints used:
//   GET  /api/document-approvals?document_id=…
//   POST /api/document-approvals/{id}/resend
//   POST /api/document-approvals/{id}/cancel

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configuration — maps backend status to UI badge styling + label.
const STATUS_CONFIG = {
  pending: { label: 'Sent', emoji: '📤', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  viewed: { label: 'Viewed', emoji: '👁', cls: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  accepted: { label: 'Approved', emoji: '✅', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  declined: { label: 'Declined', emoji: '❌', cls: 'bg-red-100 text-red-800 border-red-300' },
  review_requested: { label: 'Review Requested', emoji: '💬', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  cancelled: { label: 'Cancelled', emoji: '🚫', cls: 'bg-zinc-200 text-zinc-700 border-zinc-300' },
  expired: { label: 'Expired', emoji: '⏰', cls: 'bg-zinc-200 text-zinc-700 border-zinc-300' },
  superseded: { label: 'Superseded', emoji: '⚠', cls: 'bg-orange-100 text-orange-800 border-orange-300' },
  invalidated: { label: 'Invalidated', emoji: '⚠', cls: 'bg-orange-100 text-orange-800 border-orange-300' },
  draft: { label: 'Draft', emoji: '✏', cls: 'bg-zinc-100 text-zinc-700 border-zinc-300' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  const s = String(iso).slice(0, 19).replace('T', ' ');
  return s;
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 border text-xs font-semibold uppercase tracking-wide rounded-none ${cfg.cls}`}
      data-testid={`approval-status-badge-${status}`}
    >
      <span aria-hidden>{cfg.emoji}</span>
      <span>{cfg.label}</span>
    </span>
  );
}

export default function ApprovalStatusPanel({
  documentType, documentId, documentLabel,
  pdfUrl, // Phase 708b — when present, the panel auto-checks PDF hash
          // on mount and supersedes any prior approval whose hash differs.
  onSendNewVersion, // () => void  — opens the SendForApprovalDialog
}) {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [detailsApproval, setDetailsApproval] = useState(null); // selected approval for the Details modal
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [supersededJustNow, setSupersededJustNow] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // Phase 708b — Auto-supersede check. If the current PDF differs
      // from the latest approved version's hash, the backend flips it
      // to "superseded" so the badge below reflects reality.
      if (pdfUrl) {
        try {
          const checkRes = await axios.post(`${API}/document-approvals/auto-check-supersede`, {
            document_type: documentType,
            document_id: documentId,
            pdf_url: pdfUrl.startsWith('http') ? pdfUrl : `${process.env.REACT_APP_BACKEND_URL}${pdfUrl}`,
          });
          if (checkRes.data?.superseded) {
            setSupersededJustNow(true);
            toast.warning('Previously approved statement is now Superseded — financial data changed.', { duration: 5000 });
          }
        } catch { /* non-fatal */ }
      }
      const res = await axios.get(`${API}/document-approvals`, {
        params: { document_type: documentType, document_id: documentId },
      });
      setApprovals(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load approvals', e);
      setApprovals([]);
    } finally {
      setLoading(false);
    }
  }, [documentType, documentId, pdfUrl]);

  useEffect(() => { refresh(); }, [refresh]);

  // Phase 708d — Helpers for the panel
  // ---------------------------------
  // ① Short, friendly approval reference (APR-XXXXXX) derived from the
  //    first chars of the token. Real cryptographic token stays in the DB.
  // ② Parse user_agent into a readable Browser / OS / Device triple.
  // ③ "Sent By" should display the company sender, not the admin email.
  const COMPANY_SENDER = { name: 'CleanPro', email: 'info@cleanpro-ops.com' };
  const shortRef = (token) => `APR-${String(token || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 6).toUpperCase()}`;
  const parseUserAgent = (ua) => {
    if (!ua) return { browser: '—', os: '—', device: '—' };
    const s = ua.toLowerCase();
    let browser = 'Browser';
    if (s.includes('edg/')) browser = 'Edge';
    else if (s.includes('chrome/') && !s.includes('chromium')) browser = 'Chrome';
    else if (s.includes('safari/') && !s.includes('chrome')) browser = 'Safari';
    else if (s.includes('firefox/')) browser = 'Firefox';
    else if (s.includes('curl/')) browser = 'curl';
    let os = 'Unknown';
    if (s.includes('windows')) os = 'Windows';
    else if (s.includes('mac os x') || s.includes('macintosh')) os = 'macOS';
    else if (s.includes('iphone') || s.includes('ipad') || s.includes('ios')) os = 'iOS';
    else if (s.includes('android')) os = 'Android';
    else if (s.includes('linux')) os = 'Linux';
    let device = 'Desktop';
    if (s.includes('iphone') || (s.includes('mobile') && !s.includes('tablet'))) device = 'Mobile';
    else if (s.includes('ipad') || s.includes('tablet')) device = 'Tablet';
    return { browser, os, device };
  };

  // Phase 708d — Don't surface "Superseded" as the live status. When the
  // latest approval has been superseded (no successor sent yet), the
  // statement is effectively back to Draft for the admin — they need to
  // send a new version. Superseded approvals still appear in History.
  // The badge always reflects the most recent ACTIVE approval.
  const activeApproval = approvals.find(a =>
    !['superseded', 'invalidated'].includes(a.status));
  const latest = activeApproval || null;
  const latestStatus = latest ? latest.status : 'draft';

  const handleResend = async (approval) => {
    try {
      const res = await axios.post(`${API}/document-approvals/${approval.approval_id}/resend`);
      toast.success(res.data?.email_sent ? 'Approval email resent.' : 'Resent (email delivery unverified).');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed.');
    }
  };

  // Phase 708d — Send Reminder (#2). 24-hour throttle enforced server-side.
  const handleReminder = async (approval) => {
    try {
      const res = await axios.post(`${API}/document-approvals/${approval.approval_id}/send-reminder`);
      toast.success(`Reminder #${res.data?.reminder_count} sent.`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reminder failed.');
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    try {
      await axios.post(`${API}/document-approvals/${cancelTarget.approval_id}/cancel`, {
        reason: cancelReason,
      });
      toast.success('Approval cancelled.');
      setCancelOpen(false);
      setCancelReason('');
      setCancelTarget(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed.');
    }
  };

  return (
    <div className="bg-white border border-zinc-200 p-4" data-testid="approval-status-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700">
            Approval Status
          </h3>
          <StatusBadge status={latestStatus} />
          {latest && (
            <span className="text-[11px] font-mono text-zinc-500">
              v{latest.version}
            </span>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="rounded-none"
            onClick={() => setHistoryOpen(true)}
            disabled={loading || approvals.length === 0}
            data-testid="view-approval-history-btn"
          >
            View History ({approvals.length})
          </Button>
          {latest && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-none"
              onClick={() => setDetailsApproval(latest)}
              data-testid="view-approval-details-btn"
            >
              Approval Details
            </Button>
          )}
          {latest && ['pending', 'viewed'].includes(latestStatus) && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="rounded-none"
                onClick={() => handleResend(latest)}
                data-testid="resend-approval-btn"
              >
                Resend
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-none border-amber-400 text-amber-700 hover:bg-amber-50"
                onClick={() => handleReminder(latest)}
                data-testid="send-reminder-btn"
                title={latest.last_reminder_at ? `Last reminder: ${fmtDate(latest.last_reminder_at)} (24h throttle)` : 'Nudge the recipient'}
              >
                {latest.reminder_count ? `Send Reminder (${latest.reminder_count})` : 'Send Reminder'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-none border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => { setCancelTarget(latest); setCancelOpen(true); }}
                data-testid="cancel-approval-btn"
              >
                Cancel
              </Button>
            </>
          )}
          {onSendNewVersion && (
            <Button
              size="sm"
              className="rounded-none"
              onClick={onSendNewVersion}
              data-testid="send-new-version-btn"
            >
              {latest ? 'Send New Version' : 'Send for Approval'}
            </Button>
          )}
        </div>
      </div>

      {/* Quick info row */}
      {latest && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
          <div>
            <p className="uppercase tracking-wide text-[10px] text-zinc-400">Recipient</p>
            <p className="font-medium text-zinc-800 truncate" title={latest.owner_email}>
              {latest.owner_name || latest.owner_email}
            </p>
            {latest.owner_name && latest.owner_email && (
              <p className="text-[10px] text-zinc-500 truncate">{latest.owner_email}</p>
            )}
          </div>
          <div>
            <p className="uppercase tracking-wide text-[10px] text-zinc-400">Sent</p>
            <p className="font-mono text-zinc-800">{fmtDate(latest.sent_at)}</p>
          </div>
          <div>
            <p className="uppercase tracking-wide text-[10px] text-zinc-400">Last Action</p>
            <p className="font-mono text-zinc-800">
              {fmtDate(latest.signed_at || latest.decision_at || latest.first_viewed_at || latest.sent_at)}
            </p>
          </div>
          <div>
            <p className="uppercase tracking-wide text-[10px] text-zinc-400">Reference</p>
            <p className="font-mono text-zinc-800" title={latest.token}>
              {shortRef(latest.token)}
            </p>
          </div>
        </div>
      )}

      {/* History modal */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl rounded-none">
          <DialogHeader>
            <DialogTitle>Approval History — {documentLabel}</DialogTitle>
            <DialogDescription>
              Every version ever sent, in reverse chronological order. Records are immutable.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-zinc-200" data-testid="approval-history-list">
            {approvals.map(a => (
              <div key={a.approval_id} className="py-3 flex items-start justify-between gap-3" data-testid={`history-row-${a.version}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-xs text-zinc-500">v{a.version}</span>
                    <StatusBadge status={a.status} />
                    <span className="font-mono text-[10px] text-zinc-400">{shortRef(a.token)}</span>
                    <span className="text-xs text-zinc-500">{fmtDate(a.sent_at)}</span>
                  </div>
                  <p className="text-sm text-zinc-700">
                    To <strong>{a.owner_name || a.owner_email}</strong>
                    {a.signed_at && ` · Signed ${fmtDate(a.signed_at)}`}
                    {a.decision_comment && ` · "${a.decision_comment.slice(0, 80)}"`}
                  </p>
                </div>
                <Button size="sm" variant="ghost" className="rounded-none" onClick={() => { setDetailsApproval(a); setHistoryOpen(false); }} data-testid={`history-details-${a.version}`}>
                  Details →
                </Button>
              </div>
            ))}
            {approvals.length === 0 && (
              <p className="py-6 text-center text-sm text-zinc-500">No approvals sent yet.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Approval Details modal — full audit trail */}
      <Dialog open={!!detailsApproval} onOpenChange={(o) => !o && setDetailsApproval(null)}>
        <DialogContent className="max-w-3xl rounded-none">
          <DialogHeader>
            <DialogTitle>Approval Details — v{detailsApproval?.version}</DialogTitle>
            <DialogDescription>
              Full audit trail for this version. All fields are permanently archived.
            </DialogDescription>
          </DialogHeader>
          {detailsApproval && (
            <div className="space-y-4 max-h-[65vh] overflow-y-auto" data-testid="approval-details-content">
              {/* Summary grid */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <DetailRow label="Recipient Name" value={detailsApproval.owner_name || '—'} />
                <DetailRow label="Recipient Email" value={detailsApproval.owner_email} />
                <DetailRow label="Current Status" value={<StatusBadge status={detailsApproval.status} />} />
                <DetailRow label="Approval Reference" value={shortRef(detailsApproval.token)} mono />
                <DetailRow label="Date Sent" value={fmtDate(detailsApproval.sent_at)} mono />
                <DetailRow label="Date Viewed" value={fmtDate(detailsApproval.first_viewed_at)} mono />
                <DetailRow label="Date Approved" value={detailsApproval.status === 'accepted' ? fmtDate(detailsApproval.signed_at) : '—'} mono />
                <DetailRow label="Date Declined" value={detailsApproval.status === 'declined' ? fmtDate(detailsApproval.decision_at) : '—'} mono />
                <DetailRow label="Date Review Requested" value={detailsApproval.status === 'review_requested' ? fmtDate(detailsApproval.decision_at) : '—'} mono />
                <DetailRow label="Expires" value={fmtDate(detailsApproval.token_expires_at)} mono />
                <DetailRow label="PDF Version (SHA-256)" value={(detailsApproval.pdf_hash || '').slice(0, 16) + '…'} mono small />
                <DetailRow label="Sent By"
                  value={<><span className="font-semibold">{COMPANY_SENDER.name}</span>{' '}<span className="text-zinc-500">&lt;{COMPANY_SENDER.email}&gt;</span></>} />
              </div>

              {/* Phase 708d — Viewed-from breakdown (parsed from the
                  recipient's first-view audit entry). Surfaces IP /
                  Browser / OS / Device explicitly as the user requested. */}
              {(() => {
                const viewEntry = (detailsApproval.audit_log || []).find(e => e.action === 'viewed') ||
                                  (detailsApproval.audit_log || []).find(e => ['accepted', 'declined', 'review_requested'].includes(e.action));
                if (!viewEntry) return null;
                const ua = parseUserAgent(viewEntry.user_agent);
                return (
                  <div className="border border-zinc-200 bg-zinc-50 p-3" data-testid="viewed-from-card">
                    <p className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2 font-semibold">Viewed From</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <DetailRow label="IP Address" value={viewEntry.ip || '—'} mono />
                      <DetailRow label="Browser" value={ua.browser} />
                      <DetailRow label="Operating System" value={ua.os} />
                      <DetailRow label="Device" value={ua.device} />
                    </div>
                  </div>
                );
              })()}

              {/* Comments / decline reason */}
              {detailsApproval.decision_comment && (
                <div className="bg-zinc-50 border border-zinc-200 p-3" data-testid="approval-comment">
                  <p className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">
                    {detailsApproval.status === 'declined' ? 'Decline Reason' :
                     detailsApproval.status === 'review_requested' ? 'Review Request Comments' :
                     'Comments'}
                  </p>
                  <p className="text-sm text-zinc-800 whitespace-pre-wrap">{detailsApproval.decision_comment}</p>
                </div>
              )}
              {detailsApproval.additional_notes && (
                <div className="bg-zinc-50 border border-zinc-200 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Additional Notes</p>
                  <p className="text-sm text-zinc-800 whitespace-pre-wrap">{detailsApproval.additional_notes}</p>
                </div>
              )}

              {/* Electronic Signature */}
              {(detailsApproval.signature_method) && (
                <div className="bg-emerald-50 border border-emerald-200 p-3" data-testid="approval-signature">
                  <p className="text-[10px] uppercase tracking-wide text-emerald-700 mb-2 font-semibold">
                    Electronic Signature ({detailsApproval.signature_method})
                  </p>
                  {detailsApproval.signature_method === 'draw' && detailsApproval.signature_data && (
                    <img src={detailsApproval.signature_data} alt="Signature" className="max-h-32 border border-zinc-300 bg-white" />
                  )}
                  {detailsApproval.signature_method === 'type' && detailsApproval.signature_text && (
                    <p className="text-2xl italic font-serif text-emerald-800 px-3 py-2 bg-white border border-zinc-300">
                      {detailsApproval.signature_text}
                    </p>
                  )}
                  <p className="mt-2 text-[10px] font-mono text-emerald-700">
                    Signed at {fmtDate(detailsApproval.signed_at)}
                  </p>
                </div>
              )}

              {/* Phase 708d — Timeline view. Replaces the flat audit
                  log with a vertical timeline that is far easier to
                  scan. Each step shows the icon, action, datetime,
                  optional comment + IP/UA tucked underneath. */}
              <div>
                <p className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2 font-semibold">
                  Timeline ({(detailsApproval.audit_log || []).length} events)
                </p>
                <div className="border border-zinc-200 p-3" data-testid="approval-audit-trail">
                  <ol className="relative border-l-2 border-zinc-200 ml-3 space-y-4">
                    {(detailsApproval.audit_log || []).map((entry, idx) => {
                      const ua = parseUserAgent(entry.user_agent);
                      const ICONS = {
                        sent: '📤', viewed: '👁', accepted: '✅',
                        declined: '❌', review_requested: '💬',
                        cancelled: '🚫', expired: '⏰',
                        superseded: '⚠', invalidated: '⚠',
                        reminder_sent: '🔔',
                      };
                      const TINTS = {
                        sent: 'bg-blue-100 text-blue-800',
                        viewed: 'bg-indigo-100 text-indigo-800',
                        accepted: 'bg-emerald-100 text-emerald-800',
                        declined: 'bg-red-100 text-red-800',
                        review_requested: 'bg-amber-100 text-amber-800',
                        cancelled: 'bg-zinc-200 text-zinc-700',
                        expired: 'bg-zinc-200 text-zinc-700',
                        superseded: 'bg-orange-100 text-orange-800',
                        invalidated: 'bg-orange-100 text-orange-800',
                        reminder_sent: 'bg-amber-100 text-amber-800',
                      };
                      const tint = TINTS[entry.action] || 'bg-zinc-100 text-zinc-700';
                      const comment = entry.details && (entry.details.comment || entry.details.reason);
                      return (
                        <li key={idx} className="ml-4 relative" data-testid={`timeline-entry-${idx}`}>
                          <span className={`absolute -left-[1.4rem] flex items-center justify-center w-6 h-6 rounded-full ${tint} text-xs ring-2 ring-white`}>
                            {ICONS[entry.action] || '•'}
                          </span>
                          <div className="text-sm font-semibold capitalize text-zinc-800">
                            {String(entry.action).replace(/_/g, ' ')}
                          </div>
                          <time className="block text-[11px] text-zinc-500 font-mono">
                            {fmtDate(entry.timestamp)}
                          </time>
                          <div className="text-[11px] text-zinc-500 mt-0.5">
                            <span>by <span className="text-zinc-700">{entry.by || '—'}</span></span>
                            {entry.ip && <> · IP <span className="font-mono text-zinc-700">{entry.ip}</span></>}
                            {entry.user_agent && <> · {ua.browser} · {ua.os}</>}
                          </div>
                          {comment && (
                            <div className="mt-1 inline-block bg-zinc-50 border-l-2 border-zinc-300 px-2 py-1 text-xs text-zinc-700 italic">
                              “{comment}”
                            </div>
                          )}
                        </li>
                      );
                    })}
                    {(!detailsApproval.audit_log || detailsApproval.audit_log.length === 0) && (
                      <p className="ml-4 text-zinc-500 italic text-sm">No events recorded yet.</p>
                    )}
                  </ol>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsApproval(null)} className="rounded-none" data-testid="close-details-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation modal */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Approval Request?</DialogTitle>
            <DialogDescription>
              The recipient&apos;s review link will become inert. This action is recorded permanently in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason for cancellation (optional)"
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            className="rounded-none"
            data-testid="cancel-reason-input"
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelOpen(false)} className="rounded-none">Keep</Button>
            <Button onClick={handleCancel} className="rounded-none bg-red-600 hover:bg-red-700" data-testid="confirm-cancel-btn">
              Cancel Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({ label, value, mono = false, small = false }) {
  return (
    <div>
      <p className="uppercase tracking-wide text-[10px] text-zinc-400">{label}</p>
      <div className={`${mono ? 'font-mono' : ''} ${small ? 'text-[10px]' : 'text-xs'} text-zinc-800 break-all`}>
        {value || '—'}
      </div>
    </div>
  );
}
